import React, { useMemo, useState } from "react"
import Animated from "react-native-reanimated"
import CompsRow from "@/components/analysis/CompsRow"
import { type AnalysisType, type PropertyType } from "@/generated/graphql"
import useCompsData from "@/hooks/useCompsData"

import * as AnalyzerEvents from "@/analytics/events/analyzer"
import TextTabs, { TextTab } from "@/components/tabs/TextTabs"
import useUpdateDimming from "@/hooks/useUpdateDimming"
import { TouchableOpacity, View } from "react-native"
import BFText, { Body } from "@/components/standard/BFText"
import { useAnalyzeContext } from "@/components/analysis/AnalyzeContext"
import { CopilotStep } from "react-native-copilot"
import ToolTips from "@/constants/ToolTips"
import { WalkthroughableView } from "@/components/copilot/WalkthroughableComponents"

interface CompsVerticalScrollerProps {
  analysis: AnalysisType
}

type TabKey = "all" | "selected"

const TABS: Array<{ key: TabKey; label: string }> = [
  {
    key: "all",
    label: "  All Comps",
  },
  {
    key: "selected",
    label: "Selected Comps",
  },
]

const CompsList: React.FC<CompsVerticalScrollerProps> = ({
  analysis,
}: CompsVerticalScrollerProps) => {
  const { onCustomCompsPress } = useAnalyzeContext()
  const property: PropertyType = analysis.property
  const [selectedTab, setSelectedTab] = useState<TabKey>("all")
  const { animatedDimStyle } = useUpdateDimming()

  const {
    sortedComps,
    handleToggleSelection,
    handleFirstSelection,
    sortCriterion,
  } = useCompsData(property)

  const filteredComps = useMemo(
    () =>
      selectedTab === "all"
        ? sortedComps
        : sortedComps.filter((comp) => comp.isSelected),
    [selectedTab, sortedComps]
  )

  const { isFirstManualAnalysisDisplay, showBackflipSuggestedComps } =
    useAnalyzeContext()

  return (
    <>
      <Animated.View
        style={animatedDimStyle}
        className="flex-1 w-full mt-1 mb-4"
      >
        <TextTabs className={"mx-5"}>
          {TABS.map(({ key, label }) => {
            if (key === "selected" && !showBackflipSuggestedComps) {
              return null // Do not render "Selected Comps" tab if the flag is false
            }
            return (
              <TextTab
                label={label}
                value={key}
                key={key}
                size={"sm"}
                selected={selectedTab === key}
                onSelect={() => {
                  if (!(key === "selected" && !showBackflipSuggestedComps)) {
                    setSelectedTab(key)
                  }
                }}
              />
            )
          })}
        </TextTabs>

        <CopilotStep
          text={ToolTips.copilotStep2FindComps}
          order={2}
          name="select comp row"
          active={isFirstManualAnalysisDisplay}
        >
          <WalkthroughableView>
            {filteredComps.map((comp, index) => {
              const arvCompId = comp.arvcomp?.id
              if (!arvCompId) {
                console.error(
                  `Error: ARVComp ID is undefined for comp ID ${comp.id}.`
                )
                return null // Can also return a placeholder component if necessary
              }

              return (
                <CompsRow
                  key={
                    comp.arvcomp?.id +
                    "_" +
                    comp.arvcomp?.updatedAt +
                    "_" +
                    comp.compType
                  }
                  index={index}
                  analysis={analysis}
                  comp={comp}
                  sortCriterion={sortCriterion}
                  onFirstSelection={() => {
                    handleFirstSelection(analysis.id, arvCompId)
                    AnalyzerEvents.compAdded({
                      currentScreen: "Subject Property",
                      analysis,
                      comp: {
                        id: comp.arvcomp!.id,
                        address: comp.arvcomp?.fullAddress || "",
                      },
                    })
                  }}
                  onToggleSelection={() => {
                    handleToggleSelection(analysis.id, arvCompId)

                    if (comp.isSelected) {
                      AnalyzerEvents.compRemoved({
                        currentScreen: "Subject Property",
                        analysis,
                        comp: {
                          id: comp.arvcomp!.id,
                          address: comp.arvcomp?.fullAddress || "",
                        },
                      })
                    } else {
                      AnalyzerEvents.compAdded({
                        currentScreen: "Subject Property",
                        analysis,
                        comp: {
                          id: comp.arvcomp!.id,
                          address: comp.arvcomp?.fullAddress || "",
                        },
                      })
                    }
                  }}
                />
              )
            })}
          </WalkthroughableView>
        </CopilotStep>
      </Animated.View>
      {filteredComps.length >= 1 && filteredComps.length <= 4 && (
        <Body className="pt-2 text-center text-midnight-blue">
          Looking for more comps?{" "}
          <TouchableOpacity
            testID={"customize-comps-link-button"}
            onPress={onCustomCompsPress}
          >
            <BFText
              className="text-calm-blue underline"
              style={{ position: "relative", top: 10 }}
            >
              Customize Comps
            </BFText>
          </TouchableOpacity>{" "}
        </Body>
      )}
      <View style={{ height: 88 }} />
    </>
  )
}

export default CompsList
